import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import i18n from '../../../utils/i18n';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import lockIcon from '@adsk/bim360-matrix-react-assets/lock.svg';
import editIcon from '@adsk/bim360-matrix-react-assets/edit-icon.svg';
import iconBlock from '@adsk/bim360-matrix-react-assets/icon-block.svg';
import iconCheck from '@adsk/bim360-matrix-react-assets/icon-check.svg';
import deleteIcon from '@adsk/bim360-matrix-react-assets/trash.svg';
import {isStandardRootCauseCategory} from '../../../utils/issueRootCauses-helpers';
import IssueRootCausesAdminTitleContainer from './IssueRootCausesAdminTitleContainer';
import IssueRootCausesAdminStatus from './IssueRootCausesAdminStatus';
import {
  ISSUE_ROOT_CAUSES_PERMITTED_ACTIONS,
  ISSUE_ROOT_CAUSES_ATTRIBUTES,
  ISSUE_ROOT_CAUSES_TABLE_COLUMN_KEYS
} from '../../../constants/issue-root-causes';
import lodashValues from 'lodash/values';
import {v4 as getUUID} from 'uuid';
import {ISSUE_ATTRIBUTE_NAMES} from '../../../constants/issue-attribute-names';

class IssueRootCausesAdminTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      tableData: this.prepareTableData(props.data, props.issueRootCauseCategoriesIds),
      disableRowOnClick: false
    };

    this._renderStatus = this._renderStatus.bind(this);
    this._renderTitle = this._renderTitle.bind(this);
    this.setRowClassName = this.setRowClassName.bind(this);
    this.handleRowAction = this.handleRowAction.bind(this);
    this.getActionOptions = this.getActionOptions.bind(this);
    this.onIssueRootCauseCreate = this.onIssueRootCauseCreate.bind(this);
    this.onFinishEditRootCause = this.onFinishEditRootCause.bind(this);

    this.columns = this.getColumnProps();
    this.actionColumn = this.getActionColumnProps();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.data !== nextProps.data) {
      this.setState({
        tableData: this.prepareTableData(nextProps.data, nextProps.issueRootCauseCategoriesIds)
      });
    }
  }

  prepareTableData(data = {}, issueRootCauseCategoriesIds = []) {
    return issueRootCauseCategoriesIds
      .map(issueRootCauseCategoryId => data[issueRootCauseCategoryId])
      .map(issueRootCauseCategory => {
        const childrenIds = issueRootCauseCategory.childrenIds;
        const children = childrenIds
          ? Array.prototype.map.call(childrenIds, issueRootCauseId => data[issueRootCauseId])
          : [];
        children.push({id: getUUID(), parentId: issueRootCauseCategory.id, isNewRootCause: true});
        return {...issueRootCauseCategory, children};
      });
  }

  getActionColumnProps() {
    return {
      key: ISSUE_ROOT_CAUSES_TABLE_COLUMN_KEYS.ACTION,
      title: '',
      width: 80,
      resizable: false,
      style: {
        paddingRight: '20px'
      }
    };
  }

  getColumnProps() {
    const columns = [
      {
        key: ISSUE_ROOT_CAUSES_TABLE_COLUMN_KEYS.TITLE,
        dataKey: 'title',
        title: i18n.t('issueRootCauses.header.title'),
        width: 1200,
        resizable: false,
        cellRenderer: this._renderTitle,
        className: 'IssueRootCausesAdminTable__title-column'
      },
      {
        key: ISSUE_ROOT_CAUSES_TABLE_COLUMN_KEYS.STATUS,
        dataKey: 'isActive',
        title: i18n.t('issueRootCauses.header.status'),
        width: 400,
        resizable: false,
        cellRenderer: this._renderStatus,
        className: 'IssueRootCausesAdminTable__status-column'
      }
    ];

    return columns.map(({key, ...rest}) => <MatrixTable.Column key={key} {...rest} />);
  }

  setRowClassName({rowData, rowIndex}) {
    return cx('IssueRootCausesAdminTable__row', {
      'IssueRootCausesAdminTable__row--highlighted':
        this.props.highlightLastRow && rowIndex === this.state.tableData.length - 1,
      'IssueRootCausesAdminTable__row--child': !!rowData.parentId,
      'IssueRootCausesAdminTable__row--parent': !rowData.parentId,
      'IssueRootCausesAdminTable__row--clickable': !this.state.disableRowOnClick
    });
  }

  handleRowAction({key, rowData}) {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'delete':
        this.props.onDelete(rowData);
        break;
      case 'status':
        this.props.onStatusChange(rowData);
        break;
      case 'rename': {
        this.props.toggleTitleEditedIssueRootCauseAction(rowData.id);
        break;
      }
    }
  }

  onFinishEditRootCause() {
    this.props.toggleTitleEditedIssueRootCauseAction(null);
  }

  getActionOptions({rowData}) {
    const {
      isActive,
      isNewRootCause,
      [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ACTIONS]: permittedActions,
      [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ATTRIBUTES]: permittedAttributes
    } = rowData;
    if (isNewRootCause) {
      return [];
    }

    const canEdit = permittedActions && permittedActions.includes(ISSUE_ROOT_CAUSES_PERMITTED_ACTIONS.CAN_EDIT);
    const canDelete = permittedActions && permittedActions.includes(ISSUE_ROOT_CAUSES_PERMITTED_ACTIONS.CAN_DELETE);
    const canChangeTitle = canEdit && permittedAttributes.includes(ISSUE_ROOT_CAUSES_ATTRIBUTES.TITLE);
    const canChangeStatus =
      canEdit && permittedAttributes.includes(ISSUE_ROOT_CAUSES_ATTRIBUTES.STATUS) && this.canChangeStatus(rowData);

    const actions = [];

    canChangeTitle &&
      actions.push({
        key: 'rename',
        label: i18n.t('issueRootCauses.actions.rename'),
        svg: editIcon,
        width: '16px',
        height: '16px'
      });

    canChangeStatus &&
      actions.push({
        key: 'status',
        label: isActive
          ? i18n.t('issueRootCauses.actions.setStatusInactive')
          : i18n.t('issueRootCauses.actions.setStatusActive'),
        svg: isActive ? iconBlock : iconCheck,
        width: '16px',
        height: '16px'
      });

    canDelete &&
      actions.push({
        key: 'delete',
        label: i18n.t('issueRootCauses.actions.delete'),
        svg: deleteIcon,
        width: '16px',
        height: '16px'
      });

    return actions;
  }

  /**
   * Determines if status can be changed on category or root cause
   * User cannot inactivate the last active category
   * @param rowData
   * @returns {*}
   */
  canChangeStatus(rowData) {
    // Changing from inactive to active is always allowed
    if (!rowData.isActive) {
      return true;
    }

    // Return true only if we are trying to inactivate a category
    // and there is at least one active category other than the one we want to inactivate
    return lodashValues(this.props.data).some(
      entity => entity.isActive && !entity.parentId && entity.id !== rowData.id
    );
  }

  onIssueRootCauseCreate() {
    this.props.scrollDown({offset: 50, speed: 0.1, scrollToBottom: false});
  }

  _renderTitle({rowData, container}) {
    const {parentId, id, isNewRootCause, permittedActions, permittedAttributes} = rowData;
    const isStandardCategory = isStandardRootCauseCategory(rowData);
    const canEdit = permittedActions && permittedActions.includes(ISSUE_ROOT_CAUSES_PERMITTED_ACTIONS.CAN_EDIT);
    const canChangeTitle =
      isNewRootCause || (canEdit && permittedAttributes.includes(ISSUE_ROOT_CAUSES_ATTRIBUTES.TITLE));

    return (
      <div className="IssueRootCausesAdminTable__title-cell">
        {
          <IssueRootCausesAdminTitleContainer
            entityId={isNewRootCause ? null : id}
            clientID={isNewRootCause ? id : null}
            parentId={parentId}
            container={container}
            onFinishEdit={this.onFinishEditRootCause}
            isReadOnly={!canChangeTitle}
            onCreate={this.onIssueRootCauseCreate}
            onTitleEditStart={() => this.setState({disableRowOnClick: true})}
            onTitleEditEnd={() => {
              // setTimeout 100 is in order to actually block the onClick (0 is too fast so the onClick doesn't get blocked).
              setTimeout(() => this.setState({disableRowOnClick: false}), 100);
            }}
          />
        }
        {isStandardCategory && (
          <SvgIcon width="16px" height="14px" className="IssueRootCausesAdminTable__lock">
            {lockIcon}
          </SvgIcon>
        )}
      </div>
    );
  }

  _renderStatus({rowData, cellData: isRowActive}) {
    const {parentId, isNewRootCause} = rowData;

    if (isNewRootCause) {
      return null;
    }

    const parent = parentId && this.props.data[parentId];
    const isEnabled = parent ? parent.isActive : true;
    const isActive = parent ? parent.isActive && isRowActive : isRowActive;

    return <IssueRootCausesAdminStatus isActive={isActive} disabled={!isEnabled} />;
  }

  render() {
    return (
      <MatrixTable
        className="IssueRootCausesAdminTable"
        data={this.state.tableData}
        expandColumnKey="title"
        actionColumnProps={this.actionColumn}
        actionOptions={this.getActionOptions}
        onRowAction={this.handleRowAction}
        actionMode={MatrixTable.ActionMode.COLUMN}
        maxHeight={Number.MAX_VALUE}
        rowClassName={this.setRowClassName}
        headerClassName="IssueRootCausesAdminTable__header"
      >
        {this.columns}
      </MatrixTable>
    );
  }
}

IssueRootCausesAdminTable.displayName = 'IssueRootCausesAdminTable';

IssueRootCausesAdminTable.propTypes = {
  data: PropTypes.object.isRequired,
  scrollDown: PropTypes.func.isRequired,
  highlightLastRow: PropTypes.bool,
  issueRootCauseCategoriesIds: PropTypes.array,
  onStatusChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  toggleTitleEditedIssueRootCauseAction: PropTypes.func.isRequired
};

export default IssueRootCausesAdminTable;
